import classNames from "classnames";
import React, {useState} from "react";
import {ColumnDescription} from "react-bootstrap-table-next";
import {useTranslation} from "react-i18next";
import {SortOrder} from "../../../shared/enums/sortOrder";
import Tag from "../../atoms/tag/tag";
import PaginatedDataTable from "../../molecules/paginatedDataTable/paginatedDataTable";
import "./catalogueSearchResult.sass";
import {OptionMenuKeyValue} from "../../../stores/observables/optionMenuKeyValue";
import OptionMenu from "../../atoms/optionMenu/optionMenu";
import {ObservableList} from "../../../stores/observables/observableList";
import Icon from "../../atoms/common/icon/icon";
import {IconSource} from "../../../shared/enums/iconSource";
import {format} from "date-fns";
import EventTemplateSummaryItem from "../../../stores/domainStore/pages/models/eventTemplateSummaryItem";
import EventTemplateTitle from "../../molecules/eventTemplateTitle/eventTemplateTitle";
import {EventTemplateStatus} from "../../../shared/enums/eventTemplateStatus";
import {useStores} from "../../../stores/useStores";
import {observer} from "mobx-react-lite";
import NoResultsIcon from "../../atoms/noResultsIcon/noResultsIcon";

export interface CatalogueSearchResultProps {
  isLoading: boolean;
  onEditTemplate: (eventTemplate: EventTemplateSummaryItem) => void;
  onSelectTemplate: (eventTemplate: EventTemplateSummaryItem) => void;
  onUpdateTemplateStatusClick: (
    eventTemplate: EventTemplateSummaryItem,
    status: EventTemplateStatus
  ) => void;
  templateViewStatus: EventTemplateStatus;
}

const CatalogueSearchResult: React.FC<CatalogueSearchResultProps> = ({
  isLoading,
  onEditTemplate,
  onSelectTemplate,
  onUpdateTemplateStatusClick,
  templateViewStatus
}) => {
  const [sortField, setSortField] = useState("title");
  const [sortOrder, setSortOrder] = useState(SortOrder.Ascending);
  const {t} = useTranslation(["translation", "event", "eventTemplate"]);
  const {
    domainStore: {eventCatalogueDomainStore},
    uiStore: {eventCatalogueUiStore}
  } = useStores();

  const sortHandler = (sortField: string, sortOrder: SortOrder): void => {
    setSortField(sortField);
    setSortOrder(sortOrder);
    eventCatalogueUiStore.sortCatalogueSearchResult(sortField, sortOrder);
  };

  const headerLabelFormatter = (column: ColumnDescription<any, any>) => {
    const isColumnSorting = sortField === column.dataField;
    const isSortAscending = sortOrder === SortOrder.Ascending;
    return (
      <div
        className={classNames("event-catalogue-sortable-header", {
          "is-sort": isColumnSorting
        })}
      >
        {column.text}
        {column.sort && (
          <div>
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
            {!isColumnSorting && <Icon source={IconSource.toggleBoth} />}
          </div>
        )}
      </div>
    );
  };

  const isEnhancedCatalogue = eventCatalogueUiStore.isEnhancedCatalogue;

  const loadImageFn = async (
    template: EventTemplateSummaryItem,
    abortController?: AbortController
  ) => {
    if (!template.image) {
      return await eventCatalogueDomainStore.getEventTemplateImage(
        template.imageId,
        undefined, // we don't have a set width
        undefined, // or a set height
        undefined,
        abortController
      );
    }
    return "";
  };

  const titleFormatter = (
    _: any,
    template: EventTemplateSummaryItem,
    __: any
  ) => {
    return (
      <EventTemplateTitle
        title={template.title}
        image={template.image}
        shouldLoadImage={isEnhancedCatalogue}
        loadImageFn={(abortController) =>
          loadImageFn(template, abortController)
        }
        onClick={() => onSelectTemplate(template)}
      />
    );
  };

  const categoryFormatter = (_: any, {category}: EventTemplateSummaryItem) => {
    return category;
  };

  const descriptionFormatter = (
    _: any,
    {description}: EventTemplateSummaryItem
  ) => {
    const maxLength = 160;
    return description.length > maxLength
      ? description.substring(0, maxLength) + "..."
      : description;
  };

  const lastEditedDateFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    return format(eventTemplate.lastEditedDate, "dd/MM/yy");
  };

  const eventTemplateStatusFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    switch (eventTemplate.status) {
      case EventTemplateStatus.Active:
        return (
          <Tag type="success" text={t("eventTemplate:labels.published")} />
        );
      case EventTemplateStatus.Archived:
        return <Tag type="neutral" text={t("eventTemplate:labels.archived")} />;
    }
    return <></>;
  };

  const iconHeaderLabelFormatter = () => {
    return (
      <div className="favourite-icon">
        <Icon source={IconSource.favourite} />
      </div>
    );
  };

  const favouriteFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    return eventTemplate.isVillageFavourite ? (
      <Icon source={IconSource.favourite}></Icon>
    ) : null;
  };

  /**
   * A list of actions that can be applied to the selected event template.
   */
  var options: OptionMenuKeyValue[] = [];
  switch (templateViewStatus) {
    case EventTemplateStatus.Active:
      options.push(
        new OptionMenuKeyValue("edit", t("translation:actions.edit"), false)
      );
      options.push(
        new OptionMenuKeyValue(
          "archive",
          t("translation:actions.archive"),
          false
        )
      );
      break;
    case EventTemplateStatus.Archived:
      options.push(
        new OptionMenuKeyValue(
          "active",
          t("translation:actions.unarchive"),
          false
        )
      );
      break;
  }
  const actions = new ObservableList<OptionMenuKeyValue>(options);

  const optionMenuFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    return (
      <OptionMenu
        options={actions}
        onOptionSelected={(selectedObservableItem: OptionMenuKeyValue) => {
          switch (selectedObservableItem.value) {
            case "edit":
              onEditTemplate(eventTemplate);
              break;
            case "archive":
              onUpdateTemplateStatusClick(
                eventTemplate,
                EventTemplateStatus.Archived
              );
              break;
            case "active":
              onUpdateTemplateStatusClick(
                eventTemplate,
                EventTemplateStatus.Active
              );
              break;
            default:
              // don't know what was clicked, do nothing
              break;
          }
        }}
        disabled={false}
      />
    );
  };

  const columns: ColumnDescription<any, any>[] = [
    {
      dataField: "id",
      hidden: true,
      text: "Id"
    },
    ...(isEnhancedCatalogue
      ? [
          {
            dataField: "favourite",
            text: "",
            headerClasses: "",
            classes: "favourite-col",
            sort: false,
            headerFormatter: iconHeaderLabelFormatter,
            formatter: favouriteFormatter
          }
        ]
      : []),

    {
      dataField: "title",
      text: t("event:labels.title"),
      headerClasses: "",
      classes: "title-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: titleFormatter
    },
    {
      dataField: "catalogue",
      text: t("event:labels.category"),
      headerClasses: "",
      classes: "category-col",
      sort: false,
      headerFormatter: headerLabelFormatter,
      formatter: categoryFormatter
    },
    ...(isEnhancedCatalogue
      ? [
          {
            dataField: "description",
            text: t("event:labels.description"),
            headerClasses: "",
            classes: "description-col",
            sort: false,
            headerFormatter: headerLabelFormatter,
            formatter: descriptionFormatter
          }
        ]
      : []),
    {
      dataField: "lastEditedDate",
      text: t("event:labels.lastEditedDate"),
      headerClasses: "",
      classes: "last-edited-date-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: lastEditedDateFormatter
    },
    {
      dataField: "status",
      text: t("event:labels.eventTemplateStatus"),
      headerClasses: "",
      classes: "status-col",
      sort: false,
      headerFormatter: headerLabelFormatter,
      formatter: eventTemplateStatusFormatter
    },
    {
      dataField: "optionMenu",
      text: "",
      sort: false,
      classes: "option-menu-col",
      formatter: optionMenuFormatter
    }
  ];

  return (
    <div
      className="catalogue-search-result"
      data-testid="catalogue-search-result"
    >
      {eventCatalogueDomainStore.eventTemplates.length > 0 && (
        <div className="result-count">
          {t("event:labels.results", {
            count: eventCatalogueDomainStore.eventTemplates.length
          })}
        </div>
      )}
      {isLoading || eventCatalogueDomainStore.eventTemplates.length > 0 ? (
        <PaginatedDataTable
          data={eventCatalogueDomainStore.eventTemplates}
          keyField="id"
          columns={columns}
          sizePerPage={eventCatalogueDomainStore.eventTemplates.length}
          dataLength={eventCatalogueDomainStore.eventTemplates.length}
          onSort={sortHandler}
          remote={true}
          noBorderBottom={true}
        />
      ) : (
        <NoResultsIcon
          titleText={t("eventTemplate:messages.noSearchResults")}
          bodyText={eventCatalogueUiStore.eventCatalogueSearchText.trim()}
        />
      )}
    </div>
  );
};

export default observer(CatalogueSearchResult);
